import * as Sentry from "@sentry/react";
import { CaptureContext, SeverityLevel } from "@sentry/core";

/**
 * Reports a message to Sentry. This does **not** require an error object.
 * Use this for minor warnings or informational messages that are not critical.
 *
 * @param message - The message string to report.
 * @param extra - Optional additional context or metadata (key-value pairs) to send to Sentry.
 * @param level - Severity level of the message. Defaults to "info". Can be "log", "info", "warning", "error", "fatal".
 *
 * @example
 * // Simple info message
 * reportMessageToSentry("This is a sample message");
 *
 * @example
 * // Include extra context
 * reportMessageToSentry("We did a thing!", { someKey: "someValue" }, "log");
 *
 * @example
 * // Warning-level message
 * reportMessageToSentry("Something bad is happening, but we can recover", {}, "warning");
 */

export function reportMessageToSentry(
  message: string,
  extra?: Record<string, unknown>,
  level: SeverityLevel = "info"
) {
  Sentry.captureMessage(message, {
    level,
    extra,
  });
}

/**
 * Adds a breadcrumb to Sentry for tracing application events.
 * @param message - The breadcrumb message.
 * @param categoryOrData - Either the category string or an extra data object.
 * @param dataOrLevel - Either extra data object or severity level.
 * @param level - Severity level (default "info").
 *
 * @example
 * addSentryBreadcrumb("User login started", "auth");
 * addSentryBreadcrumb("updateUser called with empty uid", "user-update", { uid, user });
 * addSentryBreadcrumb("Navigating back", "navigation", "warning");
 * addSentryBreadcrumb("Some info with data", { foo: "bar" }, "info");
 */
export function addSentryBreadcrumb(
  message: string,
  categoryOrData?: string | Record<string, unknown>,
  dataOrLevel?: Record<string, unknown> | SeverityLevel,
  level: SeverityLevel = "info"
): void {
  let category = "default";
  let data: Record<string, unknown> | undefined;
  let finalLevel: SeverityLevel = level;

  if (typeof categoryOrData === "string") {
    category = categoryOrData;
    if (typeof dataOrLevel === "string") {
      finalLevel = dataOrLevel as SeverityLevel;
    } else {
      data = dataOrLevel as Record<string, unknown> | undefined;
    }
  } else if (categoryOrData) {
    data = categoryOrData as Record<string, unknown>;
    if (typeof dataOrLevel === "string") {
      finalLevel = dataOrLevel as SeverityLevel;
    }
  }

  Sentry.addBreadcrumb({
    category,
    message,
    level: finalLevel,
    data,
  });
}
